import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, CalendarDays, BookOpen, LifeBuoy, Sparkles, Clock, Users, Briefcase,
  FolderKanban, ClipboardCheck, Mic, PenSquare, Film,
} from "lucide-react";
import { useCampusAuth } from "@/contexts/CampusAuth";
import OnboardingChecklist from "@/components/campus/OnboardingChecklist";
import { PflegeCockpit } from "@/components/campus/PflegeCockpit";
import UpgradePromoCard from "@/components/campus/UpgradePromoCard";
import { downloadBriefingIcs, isUpcoming } from "@/lib/briefingUtils";

const NEW_VAULT = [
  {
    slug: "pflegesatz-verhandlungs-matrix",
    title: "Pflegesatz-Verhandlungs-Matrix",
    type: "XLSX + PDF · neu",
    eyebrow: "Hinzugefügt: 10. Feb 2026",
  },
  {
    slug: "entbuerokratisierung-sis-checkliste",
    title: "SIS-Entbürokratisierungs-Checkliste",
    type: "PDF · 4 Seiten",
    eyebrow: "Hinzugefügt: 09. Feb 2026",
  },
  {
    slug: "angehoerigen-beschwerde-leitfaden",
    title: "Angehörigen-Beschwerde · 5-Min-Leitfaden",
    type: "PDF · 4 Fälle",
    eyebrow: "Hinzugefügt: 09. Feb 2026",
  },
];

export default function CampusDashboard() {
  const { user } = useCampusAuth();
  const isViewer = user?.tier === "Hauptquartier" || user?.is_admin;
  const [talent, setTalent] = useState(null);
  const [myJobs, setMyJobs] = useState(null);
  const [nextBriefing, setNextBriefing] = useState(null);
  const [briefingsLoaded, setBriefingsLoaded] = useState(false);
  const [latestClip, setLatestClip] = useState(null);
  const vaultVisited = (() => {
    try { return localStorage.getItem("dh-visited-schatzkiste") === "1"; } catch { return false; }
  })();

  useEffect(() => {
    let active = true;
    const base = process.env.REACT_APP_BACKEND_URL;

    // Erste-Öffnung-Toast auf Mobile (< 768px) — nur beim allerersten Login
    try {
      const key = "dh-first-open-hint-shown";
      const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
      const alreadyShown = localStorage.getItem(key) === "1";
      if (isMobile && !alreadyShown) {
        setTimeout(() => {
          toast("Klick oben auf die rote Karte, um deine erste Patientenmappe zu öffnen.", {
            duration: 5000,
            className: "dh-first-open-toast",
          });
          localStorage.setItem(key, "1");
        }, 900);
      }
    } catch { /* localStorage nicht verfügbar → ignorieren */ }

    // Nächstes Live-Briefing laden
    axios.get(`${base}/api/briefings`, { withCredentials: true })
      .then(({ data }) => {
        if (!active) return;
        const upcoming = data.filter(isUpcoming).sort((a, b) => a.date.localeCompare(b.date));
        setNextBriefing(upcoming[0] || null);
        setBriefingsLoaded(true);
      })
      .catch(() => { if (active) setBriefingsLoaded(true); });
    if (isViewer) {
      // Träger: prüfen, ob schon eine Stelle ausgeschrieben wurde
      axios.get(`${base}/api/jobs/mine`, { withCredentials: true })
        .then(({ data }) => { if (active) setMyJobs(data); })
        .catch(() => {});
      // Neuester Recruiting-Clip (falls vorhanden)
      axios.get(`${base}/api/recruiting-clip/history`, { withCredentials: true })
        .then(({ data }) => {
          if (!active) return;
          const first = (data.items || [])[0];
          if (first) setLatestClip(first);
        })
        .catch(() => {});
    } else {
      // Pflegekräfte: prüfen, ob ein Bewerberprofil existiert
      axios.get(`${base}/api/talents/me`, { withCredentials: true })
        .then(({ data }) => { if (active) setTalent(data); })
        .catch(() => {});
    }
    return () => { active = false; };
  }, [isViewer]);

  // Geführte Schritte – abhängig von der Rolle
  const steps = isViewer
    ? [
        { id: "account", label: "Konto erstellt", done: true },
        { id: "verify", label: "E-Mail-Adresse bestätigen", desc: "Schaltet alle Campus-Funktionen frei.", done: !!user?.email_verified, action: "resend-email" },
        { id: "job", label: "Erste Stelle ausschreiben", desc: "Erreiche Pflegekräfte direkt aus unserem Netzwerk.", done: (myJobs?.length || 0) > 0, to: "/campus/app/jobs", cta: "Ausschreiben" },
        { id: "vault", label: "Schatzkiste entdecken", desc: "Geprüfte Vorlagen für MD-Prüfung, Dienstplan & Pflegesatz.", done: vaultVisited, to: "/campus/app/schatzkiste", cta: "Öffnen" },
      ]
    : [
        { id: "account", label: "Konto erstellt", done: true },
        { id: "verify", label: "E-Mail-Adresse bestätigen", desc: "Schaltet alle Campus-Funktionen frei.", done: !!user?.email_verified, action: "resend-email" },
        { id: "talent", label: "Bewerberprofil anlegen", desc: "2 Minuten – Pflegedienste & Heime melden sich direkt bei dir.", done: !!(talent && talent.is_active), to: "/campus/app/talents", cta: "Anlegen" },
        { id: "vault", label: "Schatzkiste entdecken", desc: "Vorlagen, Checklisten & Leitfäden zum Download.", done: vaultVisited, to: "/campus/app/schatzkiste", cta: "Öffnen" },
      ];

  return (
    <div className="space-y-14">
      {/* Greeting */}
      <header>
        <div className="text-[12px] uppercase tracking-[0.28em] font-medium text-sand-500 mb-3">
          Dickies-Campus  ·  Mitglieder-Bereich
        </div>
        <h1 className="font-display text-sand-800 text-3xl sm:text-[2.4rem] font-semibold leading-[1.1]">
          Schön, dass du da bist, <span className="text-terra">{user?.name?.split(" ")[0]}.</span>
        </h1>
        <p className="mt-4 text-sand-600 text-[15.5px] leading-[1.65] max-w-xl">
          {isViewer
            ? "Dein Dashboard führt dich Schritt für Schritt: Stellen ausschreiben, Personal finden, Vorlagen sichern und das nächste Live-Briefing vormerken."
            : "Dein Dashboard führt dich Schritt für Schritt: Profil anlegen, Vorlagen sichern und das nächste Live-Briefing vormerken."}
        </p>
      </header>

      {/* 0 · PDL-Cockpit · Was brennt heute? (nur bei Pflege-KI-Zugang, sonst 403 → hidden) */}
      <PflegeCockpit />

      {/* 0 · Pflege-KI Hero (Feb 2026 · Mobile-First: absolut oben, wichtigste Funktion) */}
      <Link
        to="/campus/app/patientenmappe"
        data-testid="dashboard-pflege-ki-hero"
        className="block relative overflow-hidden rounded-3xl bg-brand-darkred text-cream-50 border border-brand-darkred hover:shadow-2xl transition-all group"
      >
        <div className="absolute -right-8 -top-8 opacity-10 pointer-events-none">
          <FolderKanban className="h-56 w-56" strokeWidth={0.6} />
        </div>
        <div className="relative p-6 sm:p-8">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron/25 text-saffron px-2.5 py-0.5 text-[10.5px] font-bold tracking-widest mb-3">
            <Sparkles className="h-3 w-3" /> PFLEGE-KI · PARADYGMA-SUITE
          </div>
          <h2 className="font-display text-3xl sm:text-4xl leading-[1.05] mb-3">
            Digitale Patientenmappe<br />
            <span className="text-brand-rose">öffnen</span>
          </h2>
          <p className="text-cream-100/85 text-[14px] leading-relaxed max-w-lg mb-5">
            11 Sektionen · 26 QPR-Kriterien · SIS-Sprachdiktat · Team-Chat · Full-PDF-Export.
            <strong className="text-cream-50"> Beispiel-Mappe zum Reinklicken bereit.</strong>
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5">
            {[
              { icon: FolderKanban, t: "11 Sektionen" },
              { icon: ClipboardCheck, t: "QPR-26" },
              { icon: Mic, t: "SIS-Diktat" },
              { icon: PenSquare, t: "Signatur & PDF" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-2 text-[12px] text-cream-100/90">
                <f.icon className="h-3.5 w-3.5 shrink-0 text-brand-rose" strokeWidth={1.8} />
                <span className="truncate">{f.t}</span>
              </div>
            ))}
          </div>

          <div className="inline-flex items-center gap-2 rounded-full bg-cream-50 text-brand-darkred px-5 py-2.5 text-[13.5px] font-semibold group-hover:bg-brand-rose transition-colors">
            Patientenmappen öffnen
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>

      {/* 0b · Recruiting-Clip Hero (nur Träger/Admin · Sora-2-Video-Generator) */}
      {isViewer && (
        <Link
          to="/campus/app/recruiting-clip"
          data-testid="dashboard-recruiting-clip-hero"
          className="block relative overflow-hidden rounded-3xl bg-sand-800 text-cream-50 border border-sand-800 hover:shadow-2xl transition-all group"
        >
          <div className="absolute -right-6 -top-6 opacity-10 pointer-events-none">
            <Film className="h-52 w-52" strokeWidth={0.6} />
          </div>
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6">
            {/* Textblock */}
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron/25 text-saffron px-2.5 py-0.5 text-[10.5px] font-bold tracking-widest mb-3">
                <Sparkles className="h-3 w-3" /> NEU · KI-VIDEO · SORA 2
              </div>
              <h2 className="font-display text-2xl sm:text-[2rem] leading-[1.1] mb-2">
                Viraler <span className="text-brand-rose">Recruiting-Clip</span>
              </h2>
              <p className="text-cream-100/80 text-[13.5px] leading-relaxed max-w-lg mb-4">
                8-Sekunden-Vertikal-Video (9:16) im Skandi-Look — direkt für
                Reels, TikTok & LinkedIn. Slogan wählen, 3 Minuten warten, 1-Klick-Download.
              </p>
              <div className="inline-flex items-center gap-2 rounded-full bg-cream-50 text-sand-800 px-5 py-2.5 text-[13px] font-semibold group-hover:bg-brand-rose transition-colors">
                {latestClip && latestClip.status === "done" ? "Neuen Clip erstellen" : "Ersten Clip erstellen"}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Preview: fertiger Clip oder Preview-Icon */}
            <div className="w-full sm:w-[180px] shrink-0">
              {latestClip && latestClip.status === "done" && latestClip.preview_url ? (
                <video
                  src={`${process.env.REACT_APP_BACKEND_URL}${latestClip.preview_url}`}
                  className="w-full aspect-[9/16] rounded-xl object-cover border border-cream-100/20 shadow-lg"
                  muted
                  loop
                  autoPlay
                  playsInline
                  data-testid="dashboard-recruiting-clip-preview"
                />
              ) : (
                <div className="w-full aspect-[9/16] rounded-xl border border-cream-100/20 bg-sand-700 flex flex-col items-center justify-center gap-2 text-cream-100/70">
                  <Film className="h-8 w-8" strokeWidth={1.2} />
                  <span className="text-[11px] uppercase tracking-widest font-semibold">
                    9:16 · 8 Sek
                  </span>
                </div>
              )}
            </div>
          </div>
        </Link>
      )}

      {/* 1 · Geführte erste Schritte */}
      <OnboardingChecklist user={user} steps={steps} />

      {/* 1b · Upgrade-CTA (nur für Helden-Basic + Kurs-Bonus) */}
      <UpgradePromoCard user={user} />

      {/* 2 · Next briefing – the single most important focus block */}
      <section
        className="rounded-2xl border border-cream-300 bg-white p-7 lg:p-9"
        data-testid="dashboard-next-briefing"
      >
        <div className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-5">
          <CalendarDays className="h-3.5 w-3.5" strokeWidth={2} />
          Nächstes Live-Briefing
        </div>
        {nextBriefing ? (
          <>
            <h2 className="font-display text-sand-800 text-[1.75rem] lg:text-[2rem] font-semibold leading-[1.15]">
              {nextBriefing.title}
            </h2>
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13.5px] text-sand-600">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.6} />
                {new Date(nextBriefing.date).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" })}
                {nextBriefing.time_label ? `  ·  ${nextBriefing.time_label}` : ""}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" strokeWidth={1.6} />
                {nextBriefing.duration} · Zoom
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={1.6} />
                {nextBriefing.speaker}
              </span>
            </div>
            <div className="mt-7 flex items-center gap-5">
              <button
                onClick={() => downloadBriefingIcs(nextBriefing)}
                data-testid="dashboard-briefing-cta"
                className="inline-flex items-center gap-2 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5 text-[13.5px] font-medium transition-colors"
              >
                Im Kalender vormerken
                <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
              </button>
              <Link to="/campus/app/briefings" className="text-[13.5px] text-sand-600 underline-offset-4 hover:underline">
                Vergangene Briefings ansehen
              </Link>
            </div>
          </>
        ) : (
          <>
            <h2 className="font-display text-sand-800 text-[1.5rem] font-semibold leading-snug" data-testid="dashboard-briefing-empty">
              {briefingsLoaded ? "Der nächste Termin wird gerade geplant." : "Lade Termin …"}
            </h2>
            <p className="mt-3 text-sand-600 text-[14px] leading-relaxed max-w-lg">
              {briefingsLoaded && "Sobald das nächste Live-Briefing feststeht, siehst du es hier zuerst – inklusive Kalender-Eintrag."}
            </p>
            <div className="mt-6 flex items-center gap-5">
              <Link to="/campus/app/briefings" className="text-[13.5px] text-sand-600 underline-offset-4 hover:underline">
                Vergangene Briefings ansehen
              </Link>
              {user?.is_admin && (
                <Link to="/campus/app/briefings" data-testid="dashboard-briefing-admin-link"
                  className="text-[13.5px] font-semibold text-terra underline-offset-4 hover:underline">
                  Briefing anlegen →
                </Link>
              )}
            </div>
          </>
        )}
      </section>

      {/* 3 · Alle Bereiche – ein einziger, klarer Überblick */}
      <section data-testid="dashboard-explainer">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-sand-800 text-[1.5rem] font-semibold leading-tight">
            Dein Campus auf einen Blick
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ExplainerCard
            icon={LifeBuoy}
            title="MDK-Schutzschild nutzen"
            desc="Live-Alarm, Soforthilfe-Protokoll, fertige Antwort-Bausteine."
            to="/campus/app/lagezentrum"
          />
          <ExplainerCard
            icon={BookOpen}
            title="Schatzkiste downloaden"
            desc="Geprüfte Vorlagen — vom Dienstplan bis zur Pflegesatz-Matrix."
            to="/campus/app/schatzkiste"
          />
          <ExplainerCard
            icon={CalendarDays}
            title="Live-Briefings besuchen"
            desc="Monatlich 60 Min Zoom-Call zu Reform, MDK und Recht."
            to="/campus/app/briefings"
          />
          <ExplainerCard
            icon={Users}
            title={isViewer ? "Personal finden" : "Auf Jobsuche gehen"}
            desc={isViewer
              ? "Frisch ausgebildete Pflegekräfte und PDLs aus unserem Bildungszentrum."
              : "Kurzes Profil anlegen → Pflegedienste & Heime melden sich bei dir."}
            to="/campus/app/talents"
            highlight={!isViewer && !(talent && talent.is_active)}
          />
          <ExplainerCard
            icon={Briefcase}
            title={isViewer ? "Stelle ausschreiben" : "Offene Stellen ansehen"}
            desc={isViewer
              ? "Unbegrenzte Anzeigen für deine Einrichtung — mit Abwerbeschutz."
              : "Offene Stellen aus dem Helden-Netzwerk — direkt bewerben, ohne Umwege."}
            to="/campus/app/jobs"
          />
        </div>
      </section>

      {/* 4 · Recently added vault items */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-sand-800 text-[1.5rem] font-semibold leading-tight">
            Neu in der Schatzkiste
          </h2>
          <Link to="/campus/app/schatzkiste" className="text-[13px] text-sand-600 underline-offset-4 hover:underline">
            Alle ansehen
          </Link>
        </div>
        <ul className="divide-y divide-cream-300 border-y border-cream-300">
          {NEW_VAULT.map((v) => (
            <li key={v.slug} className="py-5 flex items-center justify-between gap-5">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
                  {v.eyebrow}
                </div>
                <div className="font-display text-sand-800 text-[1.05rem] font-semibold truncate">
                  {v.title}
                </div>
                <div className="text-[12.5px] text-sand-600 mt-0.5">{v.type}</div>
              </div>
              <Link
                to={`/campus/app/schatzkiste#${v.slug}`}
                className="shrink-0 text-[12px] uppercase tracking-[0.2em] font-bold text-terra inline-flex items-center gap-1.5"
              >
                Öffnen
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ExplainerCard({ icon: Icon, title, desc, to, highlight }) {
  return (
    <Link
      to={to}
      data-testid={`dashboard-explainer-${to.split("/").pop()}`}
      className={`group rounded-2xl border p-5 flex flex-col transition-colors ${
        highlight
          ? "bg-peach-50 border-terra/30 hover:border-terra"
          : "bg-white border-cream-300 hover:border-sand-400"
      }`}
    >
      <Icon className={`h-5 w-5 ${highlight ? "text-terra" : "text-sand-700"}`} strokeWidth={1.6} />
      <h3 className="mt-4 font-display text-[1.05rem] font-semibold text-sand-800 leading-tight">
        {title}
      </h3>
      <p className="mt-2 text-sand-600 text-[13px] leading-relaxed flex-1">{desc}</p>
      <span className={`mt-4 inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.2em] font-bold ${highlight ? "text-terra" : "text-sand-700"}`}>
        Öffnen
        <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
      </span>
    </Link>
  );
}
